import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FolderKanban, Search, Users, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicGTLPage() {
  const navigate = useNavigate();
  const [gtls, setGtls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchGTLs();
  }, []);

  const fetchGTLs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public/gtl`);
      setGtls(response.data);
    } catch (error) {
      console.error('Error fetching GTLs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'attivo':
        return <span className="badge-success">Attivo</span>;
      case 'concluso':
        return <span className="badge-neutral">Concluso</span>;
      case 'sospeso':
        return <span className="badge-warning">Sospeso</span>;
      default:
        return <span className="badge-neutral">{stato}</span>;
    }
  };

  const filteredGTLs = gtls.filter(gtl => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      gtl.nome?.toLowerCase().includes(searchLower) ||
      gtl.tema?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <img src="/logo-c3i.png" alt="C3i Logo" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Gruppi di Lavoro Temporanei</h1>
              <p className="text-slate-500 text-sm">Elenco dei GTL attivi</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="card-institutional" data-testid="public-gtl-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3">
                <FolderKanban className="w-6 h-6 text-slate-600" />
                <div>
                  <CardTitle className="font-heading text-lg">Gruppi di Lavoro</CardTitle>
                  <p className="text-sm text-slate-500">{gtls.length} gruppi totali</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome o tema..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* GTL List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredGTLs.map((gtl) => (
                  <div
                    key={gtl.id}
                    className="p-4 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => navigate(`/gtl-pubblico/${gtl.id}`)}
                    data-testid={`gtl-card-${gtl.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-900">{gtl.nome}</h3>
                          {getStatoBadge(gtl.stato)}
                        </div>
                        <p className="text-sm text-slate-600">{gtl.tema}</p>
                        <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                          <Users className="w-4 h-4" />
                          <span>{gtl.membri_count || 0} membri</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}

                {filteredGTLs.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    Nessun GTL trovato
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-slate-500">
            © 2026 C3i - Comitato Italiano Ingegneria dell'Informazione
          </p>
          <p className="text-center text-xs text-slate-400">
            Tutti i diritti riservati
          </p>
        </div>
      </div>
    </div>
  );
}
